import { Button } from "@/components/ui/button";
import { EmptyStateV2 } from "@/components/ui/empty-state-v2";
import { ErrorState } from "@/components/ui/error-state";
import { Input } from "@/components/ui/input";
import { KpiReadout } from "@/components/ui/kpi-readout";
import { PageHeaderV2 } from "@/components/ui/page-header-v2";
import { useDataset, useDatasets } from "@/features/datasets/queries";
import { useCurrentProject } from "@/features/projects/current-project";
import { useRecipe, useRecipes } from "@/features/recipes/queries";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { BookOpen } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  type TabularTaskType,
  type TrainFormState,
  buildTrainingPayload,
  validateFormState,
} from "./build-payload";
import { CapabilityMismatchCard } from "./components/CapabilityMismatchCard";
import { ForecastFieldsPanel } from "./components/ForecastFieldsPanel";
import { ModeCard } from "./components/ModeCard";
import { RecipeGuidancePicker, type RecipePick } from "./components/RecipeGuidancePicker";
import { StartTrainingError, useStartTraining } from "./mutations";
import { type Family, useTrainingOptions } from "./queries";

const FAMILIES: Family[] = ["tabular", "anomaly", "forecast"];

function initialState(datasetId: string): TrainFormState {
  return {
    datasetId,
    family: null,
    tabularTaskType: null,
    label: "",
    modelName: "",
    mode: "balanced",
    evalMetric: "",
    timeLimitMinutes: "",
    forecast: { timeCol: "", groupCol: "", predictionLength: "", frequency: "" },
  };
}

export function TrainPage() {
  const search = useSearch({ from: "/train" });
  const navigate = useNavigate();
  const { projectId } = useCurrentProject();

  const [state, setState] = useState<TrainFormState>(() => initialState(search.dataset ?? ""));
  const [submitErr, setSubmitErr] = useState<StartTrainingError | null>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<{
    id: string;
    name: string;
    version: number | null;
  } | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const { data: datasetsPage } = useDatasets(projectId);
  const readyDatasets = (datasetsPage?.items ?? []).filter((d) => d.status === "ready");
  const { data: datasetDetail } = useDataset(state.datasetId || null);
  const { data: trainingOptions } = useTrainingOptions(state.family);
  const { data: recipesPage } = useRecipes(projectId, { family: state.family ?? undefined });
  // List endpoint omits training_config — fetch detail for the picked recipe.
  const { data: pickedRecipeDetail } = useRecipe(selectedRecipe?.id ?? null);
  const startTraining = useStartTraining();

  const columns = datasetDetail?.schema ?? [];
  const errors = useMemo(() => validateFormState(state), [state]);

  const patch = (p: Partial<TrainFormState>) => setState((s) => ({ ...s, ...p }));

  const onPickRecipe = (pick: RecipePick | null) => {
    if (!pick) {
      setSelectedRecipe(null);
      return;
    }
    setSelectedRecipe({ id: pick.id, name: pick.name, version: pick.version });
  };

  // When the picked recipe's detail loads, apply its training_config to the
  // form. The list endpoint doesn't include training_config (only summary
  // fields), so the picker emits a stub and we fetch detail here.
  useEffect(() => {
    if (!pickedRecipeDetail || pickedRecipeDetail.id !== selectedRecipe?.id) return;
    const cfg = (pickedRecipeDetail.training_config ?? {}) as Record<string, unknown>;
    setState((s) => ({
      ...s,
      mode: typeof cfg.mode === "string" ? cfg.mode : s.mode,
      evalMetric: typeof cfg.eval_metric === "string" ? cfg.eval_metric : s.evalMetric,
      timeLimitMinutes:
        typeof cfg.time_limit === "number"
          ? String(Math.round(cfg.time_limit / 60))
          : s.timeLimitMinutes,
    }));
  }, [pickedRecipeDetail, selectedRecipe?.id]);

  const submit = async () => {
    if (!projectId) return;
    setHasAttemptedSubmit(true);
    if (Object.keys(errors).length > 0) {
      toast.error("Fix the highlighted fields");
      return;
    }
    setSubmitErr(null);
    try {
      const body = buildTrainingPayload(state, projectId);
      const resp = await startTraining.mutateAsync(body);
      toast.success(`Training started — ${state.modelName} rev ${resp.revision_no}`);
      navigate({ to: "/jobs/$jobId", params: { jobId: resp.job_id } });
    } catch (e) {
      if (e instanceof StartTrainingError) setSubmitErr(e);
      else toast.error("Submission failed");
    }
  };

  if (!projectId) {
    return <EmptyStateV2 title="No project selected" description="Pick a project first." />;
  }

  function fieldError(key: keyof typeof errors) {
    if (!hasAttemptedSubmit || !errors[key]) return null;
    return <p className="mt-1 text-[10px] text-status-error">{errors[key]}</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeaderV2 eyebrow="Training" title="Train a model" />

      {/* Section 1 — Dataset */}
      <section className="rounded-md border border-line bg-surface p-4">
        <h2 className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-navy-300">
          Dataset
        </h2>
        <label
          className="font-mono text-[10px] uppercase tracking-wider text-muted"
          htmlFor="dataset_picker"
        >
          Pick a dataset (ready)
        </label>
        <Input
          id="dataset_picker"
          list="train-datasets"
          value={
            readyDatasets.find((d) => d.id === state.datasetId)?.name ?? datasetDetail?.name ?? ""
          }
          onChange={(e) => {
            const pick = readyDatasets.find((d) => d.name === e.target.value);
            patch({ datasetId: pick?.id ?? e.target.value });
          }}
          placeholder="Type to search ready datasets…"
        />
        <datalist id="train-datasets">
          {readyDatasets.map((d) => (
            <option key={d.id} value={d.name} />
          ))}
        </datalist>
        {datasetDetail && (
          <div className="mt-3 flex gap-4">
            <KpiReadout label="Rows" value={(datasetDetail.row_count ?? 0).toLocaleString()} />
            <KpiReadout label="Cols" value={String(datasetDetail.column_count ?? 0)} />
          </div>
        )}
      </section>

      {/* Section 2 — Configuration */}
      <section className="rounded-md border border-line bg-surface p-4">
        <h2 className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-navy-300">
          Configuration
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              className="font-mono text-[10px] uppercase tracking-wider text-muted"
              htmlFor="family"
            >
              Family
            </label>
            <select
              id="family"
              value={state.family ?? ""}
              onChange={(e) => patch({ family: (e.target.value || null) as Family | null })}
              className="h-8 w-full rounded-md border border-border-strong bg-surface-2 px-2.5 py-1 font-mono text-xs"
            >
              <option value="">(pick one)</option>
              {FAMILIES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            {fieldError("family")}
          </div>
          {state.family === "tabular" && (
            <div>
              <label
                className="font-mono text-[10px] uppercase tracking-wider text-muted"
                htmlFor="task_type"
              >
                Task type
              </label>
              <select
                id="task_type"
                value={state.tabularTaskType ?? ""}
                onChange={(e) =>
                  patch({ tabularTaskType: (e.target.value || null) as TabularTaskType | null })
                }
                className="h-8 w-full rounded-md border border-border-strong bg-surface-2 px-2.5 py-1 font-mono text-xs"
              >
                <option value="">(pick one)</option>
                <option value="classification">classification</option>
                <option value="regression">regression</option>
                <option value="quantile">quantile</option>
              </select>
              {fieldError("tabularTaskType")}
            </div>
          )}
          {(state.family === "tabular" || state.family === "forecast") && (
            <div>
              <label
                className="font-mono text-[10px] uppercase tracking-wider text-muted"
                htmlFor="label_col"
              >
                {state.family === "forecast" ? "Value column" : "Label column"}
              </label>
              <Input
                id="label_col"
                list="schema-cols"
                value={state.label}
                onChange={(e) => patch({ label: e.target.value })}
                placeholder="column name"
              />
              <datalist id="schema-cols">
                {columns.map((c) => (
                  <option key={c.name} value={c.name} />
                ))}
              </datalist>
              {fieldError("label")}
            </div>
          )}
          <div>
            <label
              className="font-mono text-[10px] uppercase tracking-wider text-muted"
              htmlFor="model_name"
            >
              Model name
            </label>
            <Input
              id="model_name"
              value={state.modelName}
              onChange={(e) => patch({ modelName: e.target.value })}
              placeholder="e.g. churn-v1"
            />
            {fieldError("modelName")}
          </div>
          <div>
            <label
              className="font-mono text-[10px] uppercase tracking-wider text-muted"
              htmlFor="eval_metric"
            >
              Eval metric (optional)
            </label>
            <select
              id="eval_metric"
              value={state.evalMetric}
              onChange={(e) => patch({ evalMetric: e.target.value })}
              className="h-8 w-full rounded-md border border-border-strong bg-surface-2 px-2.5 py-1 font-mono text-xs"
            >
              <option value="">(engine default)</option>
              {(trainingOptions?.eval_metrics ?? []).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              className="font-mono text-[10px] uppercase tracking-wider text-muted"
              htmlFor="time_limit"
            >
              Time limit (minutes, optional)
            </label>
            <Input
              id="time_limit"
              type="number"
              min={1}
              value={state.timeLimitMinutes}
              onChange={(e) => patch({ timeLimitMinutes: e.target.value })}
              placeholder="empty = engine default"
            />
          </div>
        </div>

        {/* Modes */}
        {state.family && trainingOptions && (
          <div className="mt-4">
            <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-navy-300">
              Mode
            </p>
            <div className="grid grid-cols-3 gap-2">
              {trainingOptions.modes.map((m) => (
                <ModeCard
                  key={m.name}
                  mode={m}
                  selected={state.mode === m.name}
                  onClick={() => patch({ mode: m.name })}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Section 3 — Recipe guidance */}
      {state.family && (
        <>
          <RecipeGuidancePicker
            family={state.family}
            // Justified: RecipeOut from openapi is wider than RecipeLike; structural subset match
            recipes={(recipesPage?.items ?? []) as never}
            selectedRecipeId={selectedRecipe?.id ?? null}
            onPick={onPickRecipe}
          />
          {selectedRecipe && (
            <div className="flex items-center gap-2 rounded border border-teal-300 bg-teal-50 px-3 py-1.5 text-[11px] text-teal-900 dark:border-teal-700/40 dark:bg-teal-950/40 dark:text-teal-200">
              <BookOpen className="size-3" />
              <span>Guided by:</span>
              <span className="font-semibold">{selectedRecipe.name}</span>
              <span className="font-mono text-teal-700 dark:text-teal-300">
                v{selectedRecipe.version ?? "—"}
              </span>
            </div>
          )}
        </>
      )}

      {/* Section 4 — Forecast-specific */}
      {state.family === "forecast" && (
        <section className="rounded-md border border-line bg-surface p-4">
          <h2 className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-navy-300">
            Forecast settings
          </h2>
          <ForecastFieldsPanel
            columns={columns}
            timeCol={state.forecast.timeCol}
            groupCol={state.forecast.groupCol}
            predictionLength={state.forecast.predictionLength}
            frequency={state.forecast.frequency}
            onChange={(p) => patch({ forecast: { ...state.forecast, ...p } })}
          />
        </section>
      )}

      {/* Submit error */}
      {submitErr?.code === "capability_mismatch" && (
        <CapabilityMismatchCard
          attemptedMode={state.mode}
          alternatives={submitErr.alternatives ?? []}
          onPickAlternative={(m) => {
            patch({ mode: m });
            setSubmitErr(null);
          }}
        />
      )}
      {submitErr?.code === "validation_error" && (
        <ErrorState
          message={
            submitErr.fieldErrors?.map((f) => `${f.field}: ${f.msg}`).join(" · ") ??
            submitErr.message
          }
        />
      )}

      {/* Submit */}
      <div className="flex justify-end">
        <Button variant="primary" onClick={submit} disabled={startTraining.isPending}>
          {startTraining.isPending ? "Submitting…" : "Start training"}
        </Button>
      </div>
    </div>
  );
}
